/**
 * A210C 瓦罗兰特联名皮肤 —— Web 端数据源。
 *
 * 数据来自云端「皮肤管理」（GET /app_api/skins?product_code=A210C），
 * 只有后台**上架**的皮肤才会下发；拉取失败按空清单处理，整块隐藏。
 *
 * 与 App 端的一处关键差别：**Web 端只做预览，不做切换**。
 * 设计稿写明「网页版仅支持主题预览，可下载 Anker Dock Manager 进行切换」，
 * 所以这里不碰 fw_url / fw_size 等固件下载字段，点头像只换预览。
 */
import { fetchSkinList, type SkinItem } from '@/services/support-api'

/** 换肤过渡特效主题，目前仅用于主色，与 App 端取值保持一致 */
export type ThemeSkinFx = 'fire' | 'shadow' | 'wind' | 'nature'

export interface ThemeSkin {
  /** 稳定主键（云端皮肤 id 转字符串） */
  id: string
  /** 特工代号（英文名大写），仅作名字缺失时的兜底 */
  codeName: string
  /** 四语名称，跟随界面语言 */
  name: Record<string, string>
  /**
   * 舞台满幅底图（人物背景原画），来自云端 device_img（管理后台「设备图片」）。
   * 留空时舞台回落到主色氛围底。
   */
  artUrl?: string
  /** 方形头像，来自云端 avatar */
  avatarUrl: string
  /** 机壳屏显区轮播的预览图，来自云端 preview_shots（管理后台「预览图组」，≤5 张） */
  previewShots?: string[]
  /** 该皮肤主色，用于选中态与氛围光；接口暂无字段，统一默认色 */
  accent: string
  fx?: ThemeSkinFx
  /** 后台排序权重，小的排前面（展示顺序 = 权重升序） */
  sortWeight: number
  /** 未上架的黑色预告人物：只展示人物剪影，不可点击 */
  teaser?: boolean
  /** 该皮肤 flash 素材版本，如 "V2.0.8"，与固件文件头 "verX.Y.Z" 主号对应 */
  flashVersion?: string
}

export interface ThemeSkinList {
  skins: ThemeSkin[]
  /** 设备当前所处的皮肤（按 0x0A30 回读的 flash UI 版本主号唯一匹配得出） */
  currentSkinId: string | null
}

const PRODUCT_CODE = 'A210C'

/** 接口没有主色字段，统一用与舞台 CSS 兜底一致的默认氛围色 */
const DEFAULT_ACCENT = '#00a9e0'

function toThemeSkin(item: SkinItem): ThemeSkin {
  const name = item.name || {}
  return {
    id: String(item.id),
    codeName: (name['en-US'] || name['zh-CN'] || `#${item.id}`).toUpperCase(),
    name,
    // 管理后台口径：设备图片 = 舞台底图（人物背景原画）
    artUrl: item.device_img || undefined,
    avatarUrl: item.avatar,
    // 预览图组 = 机壳屏显区轮播的图
    previewShots: Array.isArray(item.preview_shots) ? item.preview_shots : [],
    accent: DEFAULT_ACCENT,
    sortWeight: item.sort_weight,
    flashVersion: item.flash_version,
  }
}

/**
 * 满编特工数（设计稿「条件补充」）：后台上架数到齐就说明特工已出完，
 * 队尾不再补黑色预告位；不足才补一个。CODING #41018 / #41021。
 */
const SKIN_ROSTER_SIZE = 8

/** 设计稿「条件补充」：未出齐时补的黑色预告人物，本地追加、永远压尾，不可点击 */
const TEASER_SKIN: ThemeSkin = {
  id: 'a210c-teaser',
  codeName: '?',
  name: { 'zh-CN': '？', 'en-US': '?', 'de-DE': '?', 'ja-JP': '？' },
  avatarUrl: '',
  accent: '#3a3f44',
  sortWeight: 999,
  teaser: true,
}

/**
 * 皮肤清单缓存（按 locale）。Control 页每次进出都会重新挂载主题区块，
 * 之前每次都重拉 /app_api/skins → 模块先空后"弹"出来；改为会话内只拉一次，
 * 并发调用共用同一个 in-flight Promise。只缓存成功且非空的结果，
 * 失败/空清单不缓存，下次进页还能重试。
 */
const SKIN_LIST_CACHE_TTL_MS = 10 * 60 * 1000
const skinListCache = new Map<string, { at: number; promise: Promise<ThemeSkinList> }>()

/** 拉取皮肤清单（带缓存）；locale 用于请求头 x-language（与 FAQ/手册同一套鉴权） */
export function loadThemeSkins(
  locale: string,
  uiVersion?: string | (() => string | undefined),
): Promise<ThemeSkinList> {
  const hit = skinListCache.get(locale)
  if (hit && Date.now() - hit.at < SKIN_LIST_CACHE_TTL_MS) return hit.promise
  const promise = fetchThemeSkins(locale, uiVersion).then((list) => {
    if (list.skins.length === 0) skinListCache.delete(locale)
    return list
  })
  skinListCache.set(locale, { at: Date.now(), promise })
  return promise
}

/**
 * 预取：识别到联名版后就拉清单并预热图片，用户点进 Control 页时清单与原画都已就绪。
 * 静默失败，不影响页面。
 */
export function prefetchThemeSkins(
  locale: string,
  uiVersion?: string | (() => string | undefined),
): void {
  loadThemeSkins(locale, uiVersion)
    .then((list) => preloadThemeSkinAssets(list.skins))
    .catch(() => {})
}

/** 会话内记住上次预览选中的皮肤，Control 页进出不丢选择 */
let lastSelectedSkinId: string | null = null
export function getLastSelectedSkinId(): string | null {
  return lastSelectedSkinId
}
export function setLastSelectedSkinId(id: string | null): void {
  lastSelectedSkinId = id
}

async function fetchThemeSkins(
  locale: string,
  uiVersion?: string | (() => string | undefined),
): Promise<ThemeSkinList> {
  try {
    const items = await fetchSkinList(PRODUCT_CODE, locale)
    if (items.length === 0) return { skins: [], currentSkinId: null }
    const skins = items.map(toThemeSkin)

    // 设备当前皮肤：按设备 0x0A30 回读的 flash UI 版本主号唯一匹配清单里的皮肤。
    // 请求期间版本可能晚到，只接受设备最新版本（同 adm 端口径），不做任何兜底。
    const latestVersion = typeof uiVersion === 'function' ? uiVersion() : uiVersion
    const currentSkinId = resolveDeviceSkinId(skins, latestVersion)

    return {
      // 当前主题提前到第一位，预告位压尾；特工已满编（8 个）说明没有待上架的皮肤了，队尾不再留黑格
      skins: sortSkins(
        skins.length < SKIN_ROSTER_SIZE ? [...skins, TEASER_SKIN] : skins,
        currentSkinId,
      ),
      currentSkinId,
    }
  } catch {
    // 拉不到（断网 / 接口异常）就整块隐藏，不阻塞 Control 页其他模块
    return { skins: [], currentSkinId: null }
  }
}

/** "ver2.7.1" / "V2.7.0" / "2.7" → 2；取不到返回 null（与 adm 端 parseVersionMajor 同口径） */
function parseVersionMajor(version: unknown): number | null {
  if (typeof version !== 'string') return null
  const m = /^\s*(?:ver|v)?\s*(\d+)/i.exec(version)
  return m ? Number(m[1]) : null
}

/** "ver1.1.0" / "V1.1.0" → "1.1.0"；不是纯数字版本号返回 null */
function normalizeVersion(version: unknown): string | null {
  if (typeof version !== 'string') return null
  const m = /^\s*(?:ver|v)?\s*(\d+(?:\.\d+)*)\s*$/i.exec(version)
  return m ? m[1] : null
}

/**
 * 设备 UI 版本 → 清单里的皮肤 id（与 adm 端同口径）。先按完整版本号全匹配
 * （ver1.1.0 = V1.1.0），全匹配不上再按主号唯一匹配。主号 <1（非联名素材）、
 * 读不到、匹配到多条时返回 null，不标记「使用中」。
 */
function resolveDeviceSkinId(
  skins: Pick<ThemeSkin, 'id' | 'flashVersion' | 'teaser'>[],
  uiVersion: unknown,
): string | null {
  const major = parseVersionMajor(uiVersion)
  if (major === null || major < 1) return null
  const candidates = skins.filter((s) => !s.teaser)
  const full = normalizeVersion(uiVersion)
  const exact = candidates.filter((s) => full !== null && normalizeVersion(s.flashVersion) === full)
  if (exact.length > 0) return exact.length === 1 ? exact[0].id : null
  const hits = candidates.filter((s) => parseVersionMajor(s.flashVersion) === major)
  return hits.length === 1 ? hits[0].id : null
}

/** 当前生效主题提前到第一位，预告位永远压尾，其余按权重升序（权重 = 排序顺序） */
function sortSkins(skins: ThemeSkin[], currentSkinId: string | null): ThemeSkin[] {
  return skins.slice().sort((a, b) => {
    if (a.teaser !== b.teaser) return a.teaser ? 1 : -1
    if (a.id === currentSkinId) return -1
    if (b.id === currentSkinId) return 1
    return a.sortWeight - b.sortWeight
  })
}

/** 按当前界面语言取皮肤名，缺失时回落英文再回落代号 */
export function resolveSkinName(skin: ThemeSkin, locale: string): string {
  return skin.name[locale] || skin.name['en-US'] || skin.codeName
}

/** 预加载过的地址，避免清单重拉（如切语言）时重复建 Image */
const preloaded = new Set<string>()

/**
 * 预热全部皮肤的舞台原画与机壳预览图。
 *
 * 切换预览的「卡顿」来自这里：device_img 是 MB 级原画，之前哪个皮肤
 * 第一次被点到才现从 CDN 拉，网络往返 + 解码就是可感知的延迟。
 * 清单一到就静默拉一遍，命中 HTTP 缓存后切换即时。失败不重试——
 * 真点到时 <img> 自己还会再请求一次。
 */
export function preloadThemeSkinAssets(skins: ThemeSkin[]): void {
  const urls = skins.flatMap((s) => [s.artUrl, ...(s.previewShots ?? [])])
  for (const url of urls) {
    if (!url || preloaded.has(url)) continue
    preloaded.add(url)
    const img = new Image()
    img.decoding = 'async'
    img.src = url
  }
}
